import React, { useMemo } from "react";
import type { DocumentID, DocumentMetadata } from "@ps-bridge/models/core/dataContracts";
import * as styles from "@styles/DocumentSelection.css";
import "@styles/util/DocumentSelection.global.css";
import { Download, Eye, Trash2 } from "lucide-react";

import * as IO from "@ps-bridge/interop/io";
import { useIoState } from "@utils/useIoState";
import * as PSDocumentLogic from "@ps-bridge/models/documents/documentLogic";
import { makeMilliseconds } from "@ps-bridge/models/brands/milliseconds";
import { unwrap as unwrapDocumentID } from "@ps-bridge/models/brands/documentID";
import { unwrap as unwrapIsoTimestamp } from "@ps-bridge/models/brands/isoTimestamp";
import type { IOts, OptionTs } from '@ps-bridge/interop';
import DocumentListView, { type DocumentListSectionInput } from "@view/workspace/document-selection/DocumentList.view";
import DocumentEmptyStateView from "@view/workspace/document-selection/DocumentEmptyState.view";
import * as Option from "@utils/Option";
import type { DocumentSelectionViewProps } from "@ps-bridge/contracts/documentSelection";
import { ok } from "@ps-bridge/ok";
import type { Ok } from "@ps-bridge/ok";
import { documentListSectionPhaseNamespace as documentListSectionPhase } from "@ps-bridge/models/documents/documentListSectionPhase";
import { ioEffect } from "@utils/effects/branded";
import DocumentSelectionPanel, {
  type DocumentSelectionCreatePhase,
  type DocumentSelectionListPhase,
  type DocumentSelectionModalPhase,
  type DocumentSelectionTogglePhase,
} from "@view/panels/workspace/document-selection/DocumentSelection.panel";
import DocumentSelectionItemPanel, {
  type DocumentSelectionActionPhase,
} from "@view/panels/workspace/document-selection/DocumentSelectionItem.panel";

interface AnimatedDocumentItemProps {
  doc: DocumentMetadata;
  isGridView: boolean;
  onSelect: IOts<Ok>;
  selected: boolean;
}

const AnimatedDocumentItem: React.FC<AnimatedDocumentItemProps> = ({
  doc,
  isGridView,
  onSelect,
  selected,
}) => {
  // last_edited date for each renderItem
  const formattedDate = new Date(unwrapIsoTimestamp(doc.last_edited)).toLocaleDateString();

  return (
    <div
      className={[
        styles.documentItem,
        isGridView ? styles.gridItem : styles.listItem,
        styles.fadeIn,
        selected ? styles.documentItemSelected : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <button className={styles.dropdownContainer} onClick={() => IO.run(onSelect)}>
        <div className={styles.textContainer}>
          <p className={styles.documentName}>{doc.name}</p>
          <p className={styles.documentDate}>{`Last Edited: ${formattedDate}`}</p>
        </div>
      </button>
    </div>
  );
};

const DocumentSelectionComponent: React.FC<DocumentSelectionViewProps> = ({
  onCreateDocument,
  onSelectDocument,
  availableDocuments,
  onDownloadDocument,
  onDeleteDocument,
}) => {
  const { read: isGridView, set: setIsGridView } = useIoState(false);
  const { read: selectedDocumentId, set: setSelectedDocumentId } = useIoState<OptionTs<DocumentID>>(
    Option.none<DocumentID>(),
  );
  const selectedDocumentValue = selectedDocumentId as OptionTs<DocumentID>;
  const { read: isDeleteConfirmationVisible, set: setDeleteConfirmationVisible } = useIoState(false);
  const isSome = <A,>(value: OptionTs<A>): boolean =>
    Option.fold(value, () => false, () => true);

  // Categorize documents based on their last_edited field
  const categorizedDocuments = useMemo(
    () => {
      const nowMs = Date.now();
      const withTimes = availableDocuments.map((doc) => {
        const parsed = new Date(unwrapIsoTimestamp(doc.last_edited)).getTime();
        const lastEditedMs = makeMilliseconds(Number.isFinite(parsed) ? parsed : 0);
        return { doc, lastEditedMs };
      });
      return PSDocumentLogic.categorizeDocumentsByAge(nowMs)(withTimes) as {
        editedWithin7Days: DocumentMetadata[];
        editedWithin30Days: DocumentMetadata[];
        editedBefore30Days: DocumentMetadata[];
      };
    },
    [availableDocuments]
  );
  
  const toggleDocumentSelection = (id: DocumentID) =>
    setSelectedDocumentId(
      Option.fold(selectedDocumentValue, () => Option.some(id),  (selectedId) =>
        selectedId === id ? Option.none<DocumentID>() : Option.some(id))
    );

  const renderDocumentItem = (doc: DocumentMetadata) => {
    const isSelected = Option.fold(selectedDocumentValue, () => false,  (selectedId) => selectedId === doc.id);
    const actionPanelView = (
      <div className={styles.documentActionsPanel}>
        <button
          className={styles.actionButton}
          onClick={() => {
            IO.run(onSelectDocument(doc.id));
          }}
        >
          <Eye size={16} className={styles.icon} />
          <span className={styles.actionButtonText}>View</span>
        </button>
        <button
          className={styles.actionButton}
          onClick={() => {
            IO.run(onDownloadDocument({ documentId: doc.id, title: doc.name }));
          }}
        >
          <Download size={16} className={styles.icon} />
          <span className={styles.actionButtonText}>Download</span>
        </button>
        <button
          className={styles.actionButton}
          onClick={handleDeleteConfirmation}
        >
          <Trash2 size={16} className={styles.icon} />
          <span className={styles.actionButtonText}>Delete</span>
        </button>
      </div>
    );
    const actionPhase: DocumentSelectionActionPhase = isSelected
      ? "visible"
      : "hidden";
    return (
      <div key={unwrapDocumentID(doc.id)} className={styles.documentItemWrapper}>
        <AnimatedDocumentItem
          doc={doc}
          isGridView={isGridView}
          onSelect={toggleDocumentSelection(doc.id)}
          selected={isSelected}
        />
        <DocumentSelectionItemPanel
          actionPhase={actionPhase}
          actionView={actionPanelView}
        />
      </div>
    );
  };

  // Handle delete confirmation
  const handleDeleteConfirmation = () => {
    if (isSome(selectedDocumentValue)) {
      IO.run(setDeleteConfirmationVisible(true));
    }
  };

  // Confirm deletion
  const confirmDelete = () => {
    Option.fold(selectedDocumentValue, () => undefined,  (documentId) => {
      IO.run(onDeleteDocument(documentId));
    });
    IO.run(setDeleteConfirmationVisible(false));
  };

  // Cancel deletion
  const cancelDelete = () => {
    IO.run(setDeleteConfirmationVisible(false));
  };
  const viewTogglePhase: DocumentSelectionTogglePhase = isGridView
    ? "list"
    : "grid";
  const createDocumentIo: IOts<Ok> = onCreateDocument(ok);
  const documentListSections: DocumentListSectionInput[] = [
    {
      title: "Edited within last 7 days",
      documents: categorizedDocuments.editedWithin7Days,
      phase:
        categorizedDocuments.editedWithin7Days.length > 0
          ? documentListSectionPhase.visible
          : documentListSectionPhase.hidden,
    },
    {
      title: "Edited within last 30 days",
      documents: categorizedDocuments.editedWithin30Days,
      phase:
        categorizedDocuments.editedWithin30Days.length > 0
          ? documentListSectionPhase.visible
          : documentListSectionPhase.hidden,
    },
    {
      title: "Edited more than 30 days ago",
      documents: categorizedDocuments.editedBefore30Days,
      phase:
        categorizedDocuments.editedBefore30Days.length > 0
          ? documentListSectionPhase.visible
          : documentListSectionPhase.hidden,
    },
  ];
  const listPhase: DocumentSelectionListPhase =
    availableDocuments.length === 0 ? "empty" : "list";
  const emptyView = (
    <DocumentEmptyStateView onCreateDocument={onCreateDocument} />
  );
  const listView = (
    <DocumentListView
      sections={documentListSections}
      isGridView={isGridView}
      renderDocumentItem={renderDocumentItem}
    />
  );
  const deleteModalView = (
    <div className={styles.modalOverlay} role="dialog" aria-modal="true">
      <div className={styles.modalContent}>
        <p className={styles.modalTitle}>Confirm Deletion</p>
        <p className={styles.modalMessage}>
          Are you sure you want to delete this document?
        </p>
        <div className={styles.modalActions}>
          <button onClick={cancelDelete} className={styles.modalButton}>
            Cancel
          </button>
          <button onClick={confirmDelete} className={styles.modalButton}>
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
  const deleteModalPhase: DocumentSelectionModalPhase =
    isDeleteConfirmationVisible ? "visible" : "hidden";
  const createButtonPhase: DocumentSelectionCreatePhase =
    availableDocuments.length > 0 ? "visible" : "hidden";
  const toggleViewIo: IOts<Ok> = ioEffect(() => {
    IO.run(setIsGridView(!isGridView));
    return ok;
  });
  const runPanelIO = <A,>(effect: IOts<A>): A => IO.run(effect);

  return (
    <DocumentSelectionPanel
      createButtonPhase={createButtonPhase}
      viewTogglePhase={viewTogglePhase}
      onCreateDocument={createDocumentIo}
      onToggleView={toggleViewIo}
      runIO={runPanelIO}
      listPhase={listPhase}
      listView={listView}
      emptyView={emptyView}
      deleteModalPhase={deleteModalPhase}
      deleteModalView={deleteModalView}
    />
  );
};

export default DocumentSelectionComponent;
